import { memo, useMemo } from 'react';
import type {
  WorkbookCell,
  WorkbookTable as TableData,
} from '@erp/shared-types';
import type { ColumnType } from '@erp/workbooks';
import { T } from 'gt-react';
import { WorkbookCellValue, workbookCellFont } from './WorkbookCellValue';
import { useCellMenu, type CellFilterTarget } from './WorkbookCellMenu';

/**
 * A read-only grid of a table: a Source preview or a Worksheet's Result. Its
 * Cell menu copies values, and filters `filterTarget` by them when given.
 */
export function WorkbookTable({
  table,
  types,
  filterTarget,
  percentColumns,
  groupThousands = false,
  blankCell,
  rowNumbers,
  fill = false,
}: {
  table: TableData;
  /** Fill the height left in a flex column, rather than stopping at 55vh. */
  fill?: boolean;
  /** Each shown row's position in the whole table (0-based), as a Sample's are; absent, rows are numbered from 1. */
  rowNumbers?: readonly number[];
  /** Declared types by column, so values show as the Worksheet shows them. */
  types?: ReadonlyMap<string, ColumnType>;
  /** Number columns shown as percentages, such as a pivot's shown-as Values. */
  percentColumns?: ReadonlySet<string>;
  /** Group the thousands of Numbers, as a pivot's summarized Values read. */
  groupThousands?: boolean;
  /** Cells shown blank rather than as Empty, such as a pivot's total rows' inner labels. */
  blankCell?: (row: number, column: number) => boolean;
  filterTarget?: CellFilterTarget;
}) {
  const { cellProps, menu } = useCellMenu(filterTarget);
  const layout = useTableLayout(table.columns, types, percentColumns);
  return (
    <div
      className={`overflow-auto rounded-sm border border-input bg-background ${fill ? 'min-h-64 flex-1 contain-size' : 'max-h-[55vh]'}`}
    >
      <table className="w-full border-separate border-spacing-0 text-xs tabular-nums">
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th
              scope="col"
              className="sticky left-0 z-20 w-10 border-b border-r border-input bg-muted px-2 py-1 text-muted-foreground"
            >
              #
            </th>
            {table.columns.map((column) => (
              <th
                key={column}
                scope="col"
                className={`min-w-32 border-b border-r border-input px-2 py-1 font-semibold last:border-r-0 ${types?.get(column) === 'decimal' ? 'text-right' : 'text-left'}`}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, index) => (
            <TableRow
              key={index}
              index={index}
              number={(rowNumbers?.[index] ?? index) + 1}
              cells={row}
              layout={layout}
              blank={blankKey(blankCell, index, row.length)}
              grouped={groupThousands}
              cellProps={cellProps}
            />
          ))}
        </tbody>
      </table>
      {menu}
      {table.rows.length === 0 && (
        <p className="p-3 text-muted-foreground">
          <T context="workbook empty table">No rows.</T>
        </p>
      )}
    </div>
  );
}

type TableLayout = readonly {
  name: string;
  type: ColumnType | undefined;
  percent: boolean;
}[];

/**
 * Each column's name, Declared type and percent display, kept the same
 * object while they are: callers build the types afresh on every render.
 */
function useTableLayout(
  columns: readonly string[],
  types: ReadonlyMap<string, ColumnType> | undefined,
  percentColumns: ReadonlySet<string> | undefined,
): TableLayout {
  const key = JSON.stringify(
    columns.map((name) => [
      name,
      types?.get(name) ?? null,
      percentColumns?.has(name) ?? false,
    ]),
  );
  return useMemo(() => {
    const parsed: [string, ColumnType | null, boolean][] = JSON.parse(key);
    return parsed.map(([name, type, percent]) => ({
      name,
      type: type ?? undefined,
      percent,
    }));
  }, [key]);
}

/** A row's blank cells as a string (one flag per cell), so rows compare by value. */
function blankKey(
  blankCell: ((row: number, column: number) => boolean) | undefined,
  row: number,
  length: number,
): string {
  if (!blankCell) return '';
  let key = '';
  for (let column = 0; column < length; column += 1)
    key += blankCell(row, column) ? '1' : '0';
  return key;
}

type CellProps = ReturnType<typeof useCellMenu>['cellProps'];

/** One row, memoized on the table's own row: re-shown or re-rendered tables skip unchanged rows. */
const TableRow = memo(function TableRow({
  index,
  number,
  cells,
  layout,
  blank,
  grouped,
  cellProps,
}: {
  index: number;
  /** The row's position in the whole Result: a Sample shows its rows' real numbers. */
  number: number;
  cells: readonly WorkbookCell[];
  layout: TableLayout;
  blank: string;
  grouped: boolean;
  cellProps: CellProps;
}) {
  return (
    <tr className="even:bg-muted/25 hover:bg-accent/50 last:[&>td]:border-b-0 last:[&>th]:border-b-0">
      <th
        scope="row"
        className="sticky left-0 z-[5] border-b border-r border-input bg-muted px-2 py-1 text-right font-normal text-muted-foreground"
      >
        {number}
      </th>
      {cells.map((cell, col) => {
        const column = layout[col];
        return (
          <td
            key={col}
            className={`max-w-80 truncate border-b border-r border-input px-2 py-1 outline-offset-[-2px] last:border-r-0 focus-visible:outline-2 focus-visible:outline-ring ${workbookCellFont(column?.type)}`}
            {...cellProps(index, col, cell, column?.name ?? '', column?.type)}
          >
            {blank[col] !== '1' && (
              <WorkbookCellValue
                cell={cell}
                type={column?.type}
                percent={column?.percent}
                grouped={grouped}
              />
            )}
          </td>
        );
      })}
    </tr>
  );
});
